import { ApolloQueryResult } from '@apollo/client';

import { Nullable } from '@rbi-ctg/frontend';
import { ILegacyCartEntry, IPrices } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import {
  IDayPartHours,
  IGetRestaurantQuery,
  IServiceModeHours,
  Maybe,
  OrderingStatus,
} from 'generated/graphql-gateway';
import { IUseDialogProps } from 'hooks/use-dialog-modal';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode/types';

export enum Days {
  sun,
  mon,
  tue,
  wed,
  thr,
  fri,
  sat,
}

export enum HeartbeatOverrideStatus {
  Online = 'online',
  Offline = 'offline',
  Auto = 'auto',
}

export type IRbiRestaurant = IRestaurant | IGetRestaurantQuery['restaurant'];

export type IAvailabilityProps = object & {
  available?: boolean;
  isAvailable?: boolean;
};

export type ICheckAvailability = (rbiRestaurant?: IRestaurant) => boolean;

export type IGetRestaurantFn = (storeId: string | null) => Promise<IRbiRestaurant | undefined>;

export type QueryResult = ApolloQueryResult<{ restaurant: IRbiRestaurant } | null>;

export interface IMergeRestaurantData {
  rbiRestaurant: IRestaurant;
  sanityStore: IRestaurant;
}

/**
 * The possible states of a restaurant based on:
 * 1. Availability (whether the restaurant and the restaurants service mode is configured correctly)
 * 2. The hours of the store
 */
export enum StorePermissions {
  OPEN_AND_AVAILABLE = 'openAndAvailable',
  OPEN_AND_UNAVAILABLE = 'openAndUnavailable',
  CLOSED = 'closed',
  NO_STORE_SELECTED = 'noStoreSelected',
}

// TODO: Check if this enum is used in other places. If not, unify this enum for all use cases. The diagnostic store and other upcoming Checkout 2.0 features have something similar.
// Validate after checkout 2.0 is released.
export enum StoreClosedReason {
  NO_STORE_SELECTED = 'no_store_selected',
  POS_UNAVAILABLE = 'pos_unavailable',
  MOBILE_ORDERING_DISABLED = 'mobile_ordering_disabled',
  DELIVERY_HOURS_CLOSED = 'delivery_hours_closed',
  PICKUP_HOURS_CLOSED = 'pickup_hours_closed',
  DELIVERY_FEATURE_FLAG_DISABLED = 'delivery_feature_flag_disabled',
  PICKUP_FEATURE_FLAG_DISABLED = 'pickup_feature_flag_disabled',
  SERVICE_MODE_UNAVAILABLE = 'service_mode_unavailable',
  HAS_MOBILE_ORDERING_UNAVAILABLE = 'has_mobile_ordering_unavailable',
}

export interface ISelectStoreOptions {
  sanityStore: IRestaurant;
  hasCartItems: boolean;
  callback: () => void;
  selectedOffer: any;
  requestedServiceMode?: ServiceMode;
  unavailableCartEntries?: ILegacyCartEntry[];
  skipChangeStoreMessage?: boolean;
}
interface IStoreStatusFlags {
  isStoreOpenAndAvailable: boolean;
  isStoreOpenAndUnavailable: boolean;
  isStoreClosed: boolean;
  noStoreSelected: boolean;
}

export interface OnConfirmStoreChangeParams {
  callback: () => void;
  newStore: IRestaurant;
  unavailableCartEntries?: ILegacyCartEntry[];
  message?: string;
  nextServiceMode?: ServiceMode | null;
}

export interface IStoreContext extends IStoreStatusFlags {
  email: string;
  isStoreAvailable: boolean;
  prices: IPrices;
  isPosDataLoading: boolean;
  refetchPosData: FetchPosDataFn;
  resetStore(): void;
  selectStore(options: ISelectStoreOptions): Promise<void>;
  fetchStore: (storeId: string) => Promise<void>;
  setStore: (
    store: IRestaurant | null,
    nextServiceMode?: ServiceMode | null
  ) => void | Promise<void>;
  getStoreStatusFlags: (
    store: IRestaurant | null,
    serviceMode?: Nullable<ServiceMode>
  ) => IStoreStatusFlags;
  store: IRestaurant;
  onConfirmStoreChange: IUseStore['onConfirmStoreChange'];
  onConfirmStoreChangeParams: OnConfirmStoreChangeParams | null;
  clearOnConfirmStoreChangeParams: () => void;
  isSelectingStore: boolean;
  setIsSelectingStore: React.Dispatch<React.SetStateAction<boolean>>;
  StoreChangeDialog: React.FC<IUseDialogProps>;
  availableDayParts: IDayPartBoundary[] | undefined;
  setAvailableDayParts: React.Dispatch<React.SetStateAction<IDayPartBoundary[] | undefined>>;
  isFetchingStore: boolean;
  setOnConfirmStoreChangeParams: React.Dispatch<
    React.SetStateAction<OnConfirmStoreChangeParams | null>
  >;
  setIsStoreOrderUnavailableDialogOpen: (isOpen: boolean) => void;
}

export type IUseStore = {
  onConfirmStoreChange(args: OnConfirmStoreChangeParams | undefined | null): void;
  store: IRestaurant;
  setStore: (
    store: IRestaurant | null,
    nextServiceMode?: ServiceMode | null
  ) => void | Promise<void>;
} & Pick<IStoreContext, 'resetStore'>;

export type curriedGetStoreStatus = (args: {
  store: IRestaurant;
  serviceModeStatuses: StoreServiceModeStatus[];
  selectedServiceMode?: Nullable<ServiceMode>;
}) => (serviceMode?: Nullable<ServiceMode>) => StorePermissions;

export type PosDataResults = {
  posData: Record<string, number> | null;
};

export type FetchPosDataFn = (args?: {
  storeNumber: string | null;
  isDelivery?: boolean;
}) => Promise<PosDataResults | null>;

export interface IUsePosData {
  data: PosDataResults;
  isLoading: boolean;
  refetch: FetchPosDataFn;
}

export type StoreServiceModeStatus = {
  serviceMode: ServiceMode;
  /**
   * store is available in MDM
   */
  capable: boolean;
  /**
   * store is available if store hours are within service mode availability
   * and the store supports this service mode type
   */
  available: boolean;
  /**
   * state is disabled based on flag from LD, separate from store's availability
   */
  featureFlagDisabled: boolean;
  /**
   * store hours are open within service mode availability
   */
  open?: boolean;
};

export interface IStoreDiagnosticData {
  driveThruOpen: boolean;
  diningRoomOpen: boolean;
  isRestaurantOpen: boolean;
  isMobileOrderingAvailable: boolean;
  isRestaurantPosOnline: boolean;
  mobileOrderDriveThruOpen: boolean;
  restaurantName: string;
  restaurantHasMobileOrdering: boolean;
  restaurantCountry: string;
  restaurantStreetAddress: string;
}

export interface IStoreDiagnostics {
  storeName: string;
  storeCountry: string;
  storeStreetAddress: string;
  storeId: string | undefined | null;
  storePosVendor: string | null | undefined;

  storeLocalTime: string | null;
  storeServiceModeHours: readonly IServiceModeHours[] | null;
  storeDayPartHours: readonly IDayPartHours[] | null;

  storeServiceModeStatuses: StoreServiceModeStatus[];

  storeHasMobileOrdering: boolean;
  storeMobileOrderingStatus: Maybe<OrderingStatus> | undefined;
  storeAvailableDayParts?: string;

  isStoreAvailableForPickup: boolean;
  isStoreAvailableForDelivery: boolean;
  isMobileOrderingAvailable: boolean;
  isPOSAvailable: boolean;
  isOneMenu: boolean | null;
}
